import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { MeterValue } from 'components/Meter';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { UtilizationMeter } from 'organizations/components/UtilizationMeter/UtilizationMeter';

export type OverviewLimitCardProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    title: string;
    usage: number;
    /** Effective limit; `null` renders a "No cap set" note instead of a bar. */
    limit: number | null;
    /** Unit used to format the value and limit, e.g. "minutes" or "filesize". */
    unit?: string | 'filesize';
    /** Help text shown behind an info icon next to the title. */
    info?: string;
    /** Renders as a button (utilization dialog trigger) with hover affordances. */
    interactive?: boolean;
  };

const BASE_CLASSES =
  'relative flex h-full w-full flex-col rounded-xl border border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-fg))] p-4 text-left';

/**
 * Compact utilization card of the organization utilization overview.
 * Used as trigger for the utilization limit dialogs.
 */
export const OverviewLimitCard = React.forwardRef<
  HTMLButtonElement,
  OverviewLimitCardProps
>(function OverviewLimitCard(
  { title, usage, limit, unit, info, interactive, className, ...props },
  ref
) {
  const content = (
    <>
      <Heading level="4" asChild>
        <h3 className="pr-5">
          {info ? (
            <Tooltip content={info}>
              <span className="inline-flex items-center gap-1">
                {title}
                <Icon name="help" size="xsmall" aria-label={info} />
              </span>
            </Tooltip>
          ) : (
            title
          )}
        </h3>
      </Heading>

      <div className="mt-2.5">
        <MeterValue value={usage} max={limit} unit={unit} />
      </div>

      {limit !== null && (
        <div className="mt-auto pt-3">
          <UtilizationMeter value={usage} max={limit} tone="status" />
        </div>
      )}
    </>
  );

  if (!interactive) {
    return <div className={twMerge(BASE_CLASSES, className)}>{content}</div>;
  }

  return (
    <button
      {...props}
      ref={ref}
      type="button"
      title={`Manage utilization limits for ${title.toLowerCase()}`}
      className={twMerge(
        BASE_CLASSES,
        'group cursor-pointer transition-[border-color,box-shadow] hover:border-[hsl(var(--primary-7))] hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[hsl(var(--focusRing))]',
        className
      )}
    >
      <Icon
        name="settings"
        size="small"
        className="absolute right-3 top-3 text-[hsl(var(--ui-8))] transition-colors group-hover:text-[hsl(var(--primary-9))]"
      />
      {content}
    </button>
  );
});
